import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Library, FileSpreadsheet, Sparkles, BrainCircuit,
  ShieldCheck, BarChart3, BookOpen, Layers, Award, Loader2, Lock,
  Archive, RotateCcw, Trash2, ShieldAlert,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CreateAssessmentDialog } from '@/components/studio/CreateAssessmentDialog';
import { LIFECYCLE_COLOR_MAP } from '@/lib/assetTypes';
import { ASSET_TYPE_ICONS } from '@/lib/assetTypes';
import AdminResourceCenter from '@/pages/admin/AdminResourceCenter';
import AdminQuestionnaireImport from '@/pages/admin/AdminQuestionnaireImport';
import { ModuleBanner } from '@/components/admin/ModuleBanner';
import { useApp } from '@/context/AppContext';

const TABS = [
  { value: 'library',    labelKey: 'studio.tabs.library',    fallback: 'Assessment Library', icon: Library, group: 'create' },
  { value: 'templates',  labelKey: 'studio.tabs.templates',  fallback: 'Templates',          icon: Layers, group: 'reuse' },
  { value: 'samples',    labelKey: 'studio.tabs.samples',    fallback: 'Samples',            icon: Award, group: 'reuse' },
  { value: 'import',     labelKey: 'studio.tabs.import',     fallback: 'Import Center',      icon: FileSpreadsheet, group: 'tools' },
  { value: 'knowledge',  labelKey: 'studio.tabs.knowledge',  fallback: 'Resource Center',    icon: BookOpen, group: 'tools' },
  { value: 'validation', labelKey: 'studio.tabs.validation', fallback: 'Validation',         icon: ShieldCheck, group: 'tools', planned: true },
  { value: 'analytics',  labelKey: 'studio.tabs.analytics',  fallback: 'Analytics',          icon: BarChart3, group: 'tools', planned: true },
  { value: 'ai',         labelKey: 'studio.tabs.ai',         fallback: 'AI', icon: BrainCircuit, group: 'tools', disabled: true, hint: 'Coming in Phase 9' },
];

const TAB_GROUPS = ['create', 'reuse', 'tools'];

export default function AdminStudio() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useApp();
  const activeTab = searchParams.get('tab') || 'library';
  const [createOpen, setCreateOpen] = useState(false);

  // Library state
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    adminApi.studioMeta().then(setMeta).catch(() => { /* non-fatal */ });
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (q.trim()) params.q = q.trim();
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const r = await adminApi.studioLibrary(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (err) {
      console.error('[Studio] library load failed', err);
    } finally {
      setLoading(false);
    }
  }, [q, typeFilter, statusFilter]);

  useEffect(() => {
    if (activeTab === 'library' || activeTab === 'templates' || activeTab === 'samples') {
      refresh();
    }
  }, [activeTab, refresh]);

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  const filteredItems = useMemo(() => {
    if (activeTab === 'templates') return items.filter((it) => it.is_template);
    if (activeTab === 'samples') return items.filter((it) => it.is_sample);
    return items;
  }, [activeTab, items]);

  const isAdmin = user?.role === 'hm_admin';
  const isAdvisor = user?.role === 'hm_advisor';

  // Phase 2A — governed lifecycle actions (backend-enforced; UI is convenience)
  const [archTarget, setArchTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null); // {item, dep}
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const isES = i18n.language?.startsWith('es');

  const archiveItem = async () => {
    setLifecycleBusy(true);
    try {
      await api.post(`/admin/archive/questionnaire/${archTarget.id}`, { reason_code: 'manual_archive' });
      toast.success(isES ? 'Evaluación archivada — el historial se conserva' : 'Assessment archived — history preserved', { description: archTarget.name });
      setArchTarget(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLifecycleBusy(false); }
  };

  const restoreItem = async (item) => {
    setLifecycleBusy(true);
    try {
      await api.post(`/admin/trash/questionnaire/${item.id}/restore`);
      toast.success(isES ? 'Evaluación restaurada' : 'Assessment restored', { description: item.name });
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLifecycleBusy(false); }
  };

  const preflightDelete = async (item) => {
    setLifecycleBusy(true);
    try {
      const { data: dep } = await api.get(`/admin/trash/questionnaire/${item.id}/dependencies`);
      if (dep.safe) setDelTarget({ item, dep });
      else {
        const parts = (dep.blockers || []).map((b) => `${b.count} ${b.kind.replace(/_/g, ' ')}`).join(', ');
        toast.error(isES ? 'No se puede eliminar permanentemente' : 'Cannot delete permanently', {
          description: isES
            ? `Tiene historial o dependencias (${parts}). Use "Archivar" en su lugar — la evidencia histórica nunca se destruye.`
            : `It has history or dependencies (${parts}). Use "Archive" instead — historical evidence is never destroyed.`,
        });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLifecycleBusy(false); }
  };

  const confirmDelete = async () => {
    setLifecycleBusy(true);
    try {
      await api.delete(`/admin/trash/questionnaire/${delTarget.item.id}`);
      toast.success(isES ? 'Eliminada permanentemente (respaldo + auditoría)' : 'Permanently deleted (backup + audit)', { description: delTarget.item.name });
      setDelTarget(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLifecycleBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-studio-page">
      <Topbar
        title={t('studio.pageTitle', { defaultValue: 'Assessment Studio' })}
        subtitle={t('studio.pageSubtitle', { defaultValue: 'Internal authoring workspace for CTH Admins and Advisors' })}
        actions={
          (isAdmin || isAdvisor) && (
            <Button
              onClick={() => setCreateOpen(true)}
              data-testid="studio-create-cta"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t('studio.createCta', { defaultValue: '+ Create new Assessment' })}
            </Button>
          )
        }
      />

      <div className="px-4 sm:px-6 lg:px-8 space-y-4">
        <ModuleBanner id="studio" icon={Library} />
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TooltipProvider>
            <TabsList className="flex flex-wrap h-auto p-1 gap-y-1" data-testid="studio-tabs">
              {TAB_GROUPS.map((groupKey, gi) => (
                <div
                  key={groupKey}
                  className={`flex flex-wrap items-center gap-0.5 ${gi < TAB_GROUPS.length - 1 ? 'pr-2 mr-2 border-r border-border/60' : ''}`}
                  data-testid={`studio-tab-group-${groupKey}`}
                >
                  <span className="px-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 select-none">
                    {t(`studio.tabGroups.${groupKey}`)}
                  </span>
                  {TABS.filter((tab) => tab.group === groupKey).map((tab) => {
                    const Icon = tab.icon;
                    const trigger = (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        disabled={tab.disabled}
                        data-testid={`studio-tab-${tab.value}`}
                        className="gap-1.5 text-xs"
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {t(tab.labelKey, { defaultValue: tab.fallback })}
                        {tab.planned && (
                          <Badge variant="outline" className="ml-1 px-1 py-0 text-xs uppercase tracking-wider text-muted-foreground border-muted-foreground/40">
                            {t('studio.plannedBadge', { defaultValue: 'Planned' })}
                          </Badge>
                        )}
                        {tab.disabled && <Lock className="h-3 w-3 ml-1 opacity-60" />}
                      </TabsTrigger>
                    );
                    if (tab.disabled) {
                      return (
                        <Tooltip key={tab.value}>
                          <TooltipTrigger asChild><span>{trigger}</span></TooltipTrigger>
                          <TooltipContent>{tab.hint}</TooltipContent>
                        </Tooltip>
                      );
                    }
                    return trigger;
                  })}
                </div>
              ))}
            </TabsList>
          </TooltipProvider>

          {/* Library / Templates / Samples — same renderer, different filter */}
          {['library', 'templates', 'samples'].map((tabKey) => (
            <TabsContent key={tabKey} value={tabKey} className="mt-4 space-y-3">
              <Card className="p-3 flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[220px]">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input
                      data-testid="studio-search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && refresh()}
                      placeholder={t('studio.searchPlaceholder', { defaultValue: 'Search assessments…' })}
                      className="pl-8"
                    />
                  </div>
                </div>
                {tabKey === 'library' && (
                  <>
                    <div className="w-40">
                      <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
                        <SelectTrigger data-testid="studio-filter-type">
                          <SelectValue placeholder={t('studio.filter.type', { defaultValue: 'All types' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('studio.filter.all', { defaultValue: 'All types' })}</SelectItem>
                          {(meta?.asset_types || []).map((a) => (
                            <SelectItem key={a.code} value={a.code}>
                              {i18n.language?.startsWith('es') ? a.label_es : a.label_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-40">
                      <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                        <SelectTrigger data-testid="studio-filter-status">
                          <SelectValue placeholder={t('studio.filter.status', { defaultValue: 'All stages' })} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('studio.filter.allStages', { defaultValue: 'All stages' })}</SelectItem>
                          {(meta?.lifecycle_stages || []).map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {i18n.language?.startsWith('es') ? s.label_es : s.label_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <Button onClick={refresh} variant="outline" data-testid="studio-refresh">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.refresh', { defaultValue: 'Refresh' })}
                </Button>
              </Card>

              {loading ? (
                <Card className="p-12 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                </Card>
              ) : filteredItems.length === 0 ? (
                tabKey === 'templates' || tabKey === 'samples' ? (
                  <Card className="p-12 text-center" data-testid={`studio-${tabKey}-empty`}>
                    {tabKey === 'templates'
                      ? <Layers className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                      : <Award className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />}
                    <p className="text-sm font-semibold mb-1">
                      {t(`studio.${tabKey}Tab.emptyTitle`)}
                    </p>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      {t(`studio.${tabKey}Tab.emptyBody`)}
                    </p>
                  </Card>
                ) : (
                  <Card className="p-12 text-center" data-testid="studio-library-empty">
                    <Library className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {t('studio.library.empty', { defaultValue: 'No assessments match the current filters.' })}
                    </p>
                  </Card>
                )
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="studio-library-grid">
                  {filteredItems.map((item) => (
                    <AssessmentCard
                      key={item.id}
                      item={item}
                      onClick={() => navigate(`/admin/studio/edit/${item.id}`)}
                      meta={meta}
                      isAdmin={isAdmin}
                      busy={lifecycleBusy}
                      onArchive={() => setArchTarget(item)}
                      onRestore={() => restoreItem(item)}
                      onDelete={() => preflightDelete(item)}
                    />
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {tabKey === 'library'
                  ? t('studio.library.count', { defaultValue: '{{count}} of {{total}} shown', count: filteredItems.length, total })
                  : t('studio.library.countTab', { defaultValue: '{{count}} shown in this view', count: filteredItems.length })}
              </p>
            </TabsContent>
          ))}

          {/* Import Center — re-uses the existing wizard */}
          <TabsContent value="import" className="mt-4">
            <AdminQuestionnaireImport embedded />
          </TabsContent>

          {/* Resource Center — the same shared module, embedded for convenience */}
          <TabsContent value="knowledge" className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground px-1" data-testid="studio-knowledge-note">
              {t('studio.knowledgeNote', { defaultValue: 'This is the shared Resource Center — the same module available from the sidebar.' })}
            </p>
            <AdminResourceCenter embedded />
          </TabsContent>

          {/* Validation — Phase 1 placeholder with Sprint A.1.3-γ context */}
          <TabsContent value="validation" className="mt-4">
            <Card className="p-8" data-testid="studio-validation-tab">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-6 w-6 text-primary mt-1" />
                <div>
                  <h3 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
                    {t('studio.validation.title', { defaultValue: 'Validation Center' })}
                    <Badge variant="outline" className="text-xs uppercase tracking-wider text-muted-foreground">
                      {t('studio.plannedBadge', { defaultValue: 'Planned' })}
                    </Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-prose">
                    {t('studio.validation.desc', {
                      defaultValue:
                        'Imports are validated automatically via the Import Wizard. Cross-asset semantic validation (weights, language consistency, completeness checks) will surface here in Phase 2.',
                    })}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => handleTabChange('import')}
                    data-testid="studio-validation-goto-import"
                  >
                    {t('studio.validation.gotoImport', { defaultValue: 'Open Import Center' })}
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Analytics — Phase 1 placeholder */}
          <TabsContent value="analytics" className="mt-4">
            <Card className="p-8" data-testid="studio-analytics-tab">
              <div className="flex items-start gap-3">
                <BarChart3 className="h-6 w-6 text-primary mt-1" />
                <div>
                  <h3 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
                    {t('studio.analytics.title', { defaultValue: 'Assessment Analytics' })}
                    <Badge variant="outline" className="text-xs uppercase tracking-wider text-muted-foreground">
                      {t('studio.plannedBadge', { defaultValue: 'Planned' })}
                    </Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-prose">
                    {t('studio.analytics.desc', {
                      defaultValue:
                        'Cross-assessment analytics — usage funnels, completion rates, score distributions, and assessment health — land in Phase 4.',
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground max-w-prose mt-2" data-testid="studio-analytics-planned-note">
                    {t('studio.analytics.plannedNote', { defaultValue: 'This is planned functionality — nothing is missing or broken.' })}
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* AI — disabled in Phase 1 */}
          <TabsContent value="ai" className="mt-4">
            <Card className="p-8" data-testid="studio-ai-tab">
              <div className="flex items-start gap-3">
                <Sparkles className="h-6 w-6 text-primary mt-1" />
                <div>
                  <h3 className="font-display text-lg font-bold mb-1">
                    {t('studio.ai.title', { defaultValue: 'AI Authoring' })}
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-prose">
                    {t('studio.ai.desc', { defaultValue: 'Coming in Phase 9 — generate assessments from job descriptions, competencies, or natural-language prompts.' })}
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <CreateAssessmentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => refresh()}
      />

      {/* Phase 2A — Archive confirmation (normal) */}
      <Dialog open={!!archTarget} onOpenChange={(v) => !v && setArchTarget(null)}>
        <DialogContent className="max-w-md" data-testid="studio-archive-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-muted-foreground" />
              {isES ? 'Archivar evaluación' : 'Archive assessment'}
            </DialogTitle>
            <DialogDescription>
              {isES
                ? `"${archTarget?.name}" dejará de estar activa para nuevo uso operativo, pero todo su historial (asignaciones, respuestas, resultados e informes) se conserva intacto. Puede restaurarse.`
                : `"${archTarget?.name}" will no longer be active for new operational use, but all its history (assignments, responses, results and reports) is fully preserved. It can be restored.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchTarget(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={archiveItem} disabled={lifecycleBusy} data-testid="studio-archive-confirm-btn">
              {lifecycleBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isES ? 'Archivar' : 'Archive')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2A — Hard delete: strong destructive confirmation, visually distinct */}
      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent className="max-w-md border-destructive/40" data-testid="studio-delete-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" />
              {isES ? 'ELIMINACIÓN PERMANENTE' : 'PERMANENT DELETION'}
            </DialogTitle>
            <DialogDescription>
              {isES
                ? `"${delTarget?.item?.name}" nunca ha sido usada: 0 asignaciones, 0 respuestas, 0 resultados, 0 informes y 0 referencias activas (verificado por el servidor). Se creará un respaldo y la eliminación quedará auditada. ESTA ACCIÓN ES IRREVERSIBLE.`
                : `"${delTarget?.item?.name}" has never been used: 0 assignments, 0 responses, 0 results, 0 reports and 0 active references (server-verified). A backup is stored and the deletion is audited. THIS ACTION IS IRREVERSIBLE.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelTarget(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={lifecycleBusy} data-testid="studio-delete-confirm-btn">
              {lifecycleBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isES ? 'Eliminar permanentemente' : 'Delete permanently')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssessmentCard({ item, onClick, meta, isAdmin, busy, onArchive, onRestore, onDelete }) {
  const { t, i18n } = useTranslation();
  const status = item.status || 'draft';
  const color = LIFECYCLE_COLOR_MAP[status] || LIFECYCLE_COLOR_MAP.draft;
  const stage = meta?.lifecycle_stages?.find((s) => s.value === status);
  const stageLabel = stage ? (i18n.language?.startsWith('es') ? stage.label_es : stage.label_en) : status;
  const Icon = ASSET_TYPE_ICONS[item.type] || Library;
  const typeMeta = meta?.asset_types?.find((a) => a.code === item.type);
  const typeLabel = typeMeta ? (i18n.language?.startsWith('es') ? typeMeta.label_es : typeMeta.label_en) : item.type;
  const isES = i18n.language?.startsWith('es');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      data-testid={`studio-asset-row-${item.id}`}
      className="text-left cursor-pointer"
    >
      <Card className="p-4 h-full hover:shadow-md hover:border-primary/30 transition-all">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="rounded-md bg-muted p-1.5 shrink-0">
              <Icon className="h-3.5 w-3.5" />
            </div>
            <span className="text-xs text-muted-foreground uppercase tracking-wider truncate">
              {typeLabel || t('studio.untyped', { defaultValue: 'Generic' })}
            </span>
          </div>
          <Badge
            variant="outline"
            className={`${color.bg} text-xs font-semibold uppercase tracking-wide`}
            data-testid={`studio-row-status-${status}`}
          >
            {stageLabel}
          </Badge>
        </div>
        <h3 className="font-semibold text-sm leading-snug mb-1 truncate">
          {item.name}
        </h3>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {item.description || t('studio.noDescription', { defaultValue: 'No description.' })}
        </p>
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          {item.type !== 'profile' && (
            <span>{t('studio.row.difficulty', { defaultValue: 'D{{n}}', n: item.difficulty ?? 3 })}</span>
          )}
          {item.passing_score != null && <span>· {item.passing_score}%</span>}
          {item.estimated_minutes != null && <span>{item.type !== 'profile' ? '· ' : ''}{item.estimated_minutes}m</span>}
          {isAdmin && (
            <span className="ml-auto flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              {status !== 'archived' ? (
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground" disabled={busy}
                        title={isES ? 'Archivar (conserva el historial)' : 'Archive (history preserved)'}
                        onClick={onArchive} data-testid={`studio-archive-${item.id}`}>
                  <Archive className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-700" disabled={busy}
                        title={isES ? 'Restaurar' : 'Restore'}
                        onClick={onRestore} data-testid={`studio-restore-${item.id}`}>
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" disabled={busy}
                      title={isES ? 'Eliminar permanentemente (solo sin historial)' : 'Delete permanently (zero-history only)'}
                      onClick={onDelete} data-testid={`studio-delete-${item.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
